import type { MfaMethod } from "@opensea/embedded-wallet-sdk"
import {
  initMfaEnrollmentWithTotp,
  submitMfaEnrollmentWithSms,
  submitMfaEnrollmentWithTotp,
} from "@opensea/embedded-wallet-sdk"
import { proxy, ref } from "valtio"
import { deepClone } from "valtio/utils"
import { UnreachableCaseError } from "../lib/errors"
import { MfaHandlerActions } from "./mfaHandlerStore"

// -- Types ---------------------------------------------------------
type MfaEnrollmentStepId =
  | "select-enrollment-option"
  | "enroll-sms"
  | "enroll-totp"

type EnrollTotpStep = {
  id: "enroll-totp"
  authUrl: string
  secret: string
}

type MfaEnrollmentStep =
  | EnrollTotpStep
  | { id: Exclude<MfaEnrollmentStepId, "enroll-totp"> }

type SubmitMFAEnrollmentProps =
  | { code: string }
  | {
      code: string
      phoneNumber: string
    }

type MfaEnrollmentStateType = {
  step: MfaEnrollmentStep
  submitting: boolean
  submissionError?: string
  initializingStep?: MfaEnrollmentStepId
  onSuccess?: () => void
}

// -- State ---------------------------------------------------------
const INITIAL_STATE: MfaEnrollmentStateType = {
  step: { id: "select-enrollment-option" },
  submitting: false,
  submissionError: undefined,
  initializingStep: undefined,
  onSuccess: undefined,
}

export const MfaEnrollmentState = proxy<MfaEnrollmentStateType>(
  deepClone(INITIAL_STATE),
)

// -- Actions -------------------------------------------------------
export const MfaEnrollmentActions = {
  async selectEnrollmentOption(mfaMethod: MfaMethod) {
    switch (mfaMethod) {
      case "sms":
        MfaEnrollmentState.step = { id: "enroll-sms" }
        break
      case "totp":
        MfaEnrollmentState.initializingStep = "enroll-totp"
        {
          const { authUrl, secret } = await initMfaEnrollmentWithTotp()
          MfaEnrollmentState.step = { id: "enroll-totp", authUrl, secret }
          MfaEnrollmentState.initializingStep = undefined
        }
        break
      case "passkey":
        // Handle passkey enrollment if needed
        break
      default:
        throw new UnreachableCaseError(mfaMethod)
    }
  },

  async submitEnrollment(props: SubmitMFAEnrollmentProps) {
    const mfaMethod =
      MfaEnrollmentState.step.id === "enroll-sms" ? "sms" : "totp"
    MfaEnrollmentState.submitting = true
    try {
      switch (mfaMethod) {
        case "sms":
          if (!("phoneNumber" in props)) {
            throw new Error("Phone number is required for SMS enrollment")
          }
          await submitMfaEnrollmentWithSms(props.phoneNumber, props.code)
          MfaHandlerActions.completeEnrollment()
          break
        case "totp":
          await submitMfaEnrollmentWithTotp(props.code)
          MfaHandlerActions.completeEnrollment()
          break
        default:
          throw new UnreachableCaseError(mfaMethod)
      }
      // Call the onSuccess callback and reset it
      MfaEnrollmentState.onSuccess?.()
      MfaEnrollmentState.onSuccess = undefined
    } catch (error) {
      if (error instanceof UnreachableCaseError) {
        throw error
      }
      MfaEnrollmentState.submissionError = "Invalid authentication code."
    } finally {
      MfaEnrollmentState.submitting = false
    }
  },

  setSubmissionError(error?: string) {
    MfaEnrollmentState.submissionError = error
  },

  onBack() {
    MfaEnrollmentState.step = { id: "select-enrollment-option" }
  },

  reset() {
    MfaEnrollmentState.step = { id: "select-enrollment-option" }
    MfaEnrollmentState.submitting = false
    MfaEnrollmentState.submissionError = undefined
    MfaEnrollmentState.initializingStep = undefined
  },

  startEnrollment(mfaMethod?: MfaMethod, onSuccess?: () => void) {
    if (mfaMethod) {
      MfaEnrollmentActions.selectEnrollmentOption(mfaMethod)
    }
    MfaHandlerActions.startMfaEnrollment()
    if (onSuccess) {
      MfaEnrollmentState.onSuccess = ref(onSuccess)
    } else {
      MfaEnrollmentState.onSuccess = undefined
    }
  },
}
